import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { WindowRef } from '@boels/helpers';
import {
  AVAILABLE_LANGUAGES,
  BoelsBaseSite,
  BoelsBaseSiteEntities,
  BoelsBaseSiteLanguage,
  AppConfig,
  LanguageEnum,
  PageType,
} from '@boels/models';
import {
  CmsFacade,
  CultureFacade,
  SiteContextFacade,
} from '@boels/shared-state';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { catchError, EMPTY, first, map, Observable, Subscription } from 'rxjs';
import { SpinnerComponent } from '../spinner/spinner.component';
import { LanguageSelectorPageItemComponent } from './language-selector-page-item/language-selector-page-item.component';

interface UserGeoData {
  countryName: string;
  countryCode: string;
}

@Component({
  selector: 'boels-language-selector-page',
  templateUrl: './language-selector-page.component.html',
  styleUrls: ['./language-selector-page.component.scss'],
  imports: [
    LanguageSelectorPageItemComponent,
    SpinnerComponent,
    AsyncPipe,
    TranslocoModule,
    AsyncPipe,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['language-selector-page'],
    },
  ],
})
export class LanguageSelectorPageComponent implements OnInit, OnDestroy {
  private siteContextFacade = inject(SiteContextFacade);
  private httpClient = inject(HttpClient);
  private router = inject(Router);
  private cmsFacade = inject(CmsFacade);
  private cultureFacade = inject(CultureFacade);
  protected environment = inject<AppConfig>('ENVIRONMENT' as any);
  winRef = inject(WindowRef);

  private readonly ipServiceUrl = `${this.environment?.dbIp.baseUrl}/${this.environment?.dbIp.apiKey}/self`;
  private readonly subcriptions = new Subscription();

  baseSites$: Observable<Array<BoelsBaseSite>>;
  currentBaseSite$: Observable<BoelsBaseSite | undefined>;
  urlAfterRedirects: string;
  redirectUrl: string | null = null;
  queryParams: string;

  ngOnInit(): void {
    this.setCountryAndLoadData('en');
    if (this.winRef.isBrowser()) {
      this.subcriptions.add(this.getUserGeoData());
    }

    this.subcriptions.add(
      this.router.routerState.root.queryParams.subscribe((params) => {
        this.redirectUrl = params['redirect'] || null;
        this.queryParams = Object.keys(params)
          .filter((key) => key !== 'redirect')
          .map((key) => {
            const value = params[key];
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
          })
          .join('&');
      }),
    );

    this.baseSites$ = this.siteContextFacade.getBaseSites().pipe(
      map((boelsBaseSites: BoelsBaseSiteEntities) =>
        Object.values(boelsBaseSites)
          .filter(
            (boelsBaseSite: BoelsBaseSite) =>
              boelsBaseSite.country && boelsBaseSite.showOnLanguageSelector,
          )
          .sort(
            (actualBoelsBaseSite, nextBoelsBaseSite) =>
              actualBoelsBaseSite.country?.nativeName?.localeCompare(
                nextBoelsBaseSite.country?.nativeName as string,
              ) as number,
          ),
      ),
    );

    this.currentBaseSite$ = this.siteContextFacade.getActiveBaseSiteEntity();
  }

  ngOnDestroy(): void {
    this.subcriptions.unsubscribe();
  }

  private setCountryAndLoadData(countryCode: string) {
    this.cultureFacade.setGenericContext(countryCode.toLowerCase());
    this.siteContextFacade.loadBaseSites();
    this.cmsFacade.loadPage('countrySelection', PageType.CONTENT_PAGE);
  }

  trackByUid(index: number, boelsBaseSite: BoelsBaseSite): string {
    return boelsBaseSite.uid as string;
  }

  setBaseSiteLanguage(baseSiteLanguage: BoelsBaseSiteLanguage): void {
    const cultureIsoCode: string =
      baseSiteLanguage.language?.isocode?.substring(0, 2) +
      '-' +
      baseSiteLanguage.baseSite?.country?.isocode?.toLowerCase();

    if (this.winRef.location) {
      this.winRef.location.href =
        cultureIsoCode +
        `${this.redirectUrl ? decodeURIComponent(this.redirectUrl) : ''}` +
        `${this.queryParams ? '?' + decodeURIComponent(this.queryParams) : ''}`;
    }
  }

  private getUserGeoData(): Subscription {
    return this.httpClient
      .get<UserGeoData>(this.ipServiceUrl)
      .pipe(
        first(),
        catchError(() => {
          return EMPTY;
        }),
      )
      .subscribe((userGeoData: UserGeoData) => {
        const countryCode = userGeoData.countryCode?.toLowerCase();
        if (
          countryCode &&
          AVAILABLE_LANGUAGES.includes(countryCode as LanguageEnum)
        ) {
          this.setCountryAndLoadData(countryCode);
        }
      });
  }
}
